import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import { FileText, Copy, Download, RefreshCw, Check, Edit2, Save, AlertCircle, Sparkles, Brain } from 'lucide-react'
import { trackEvent, ANALYTICS_EVENTS } from '../lib/analytics'

export default function Llms() {
  const { activeBrand } = useAuth()
  const { isDemoMode } = useDemoMode()
  const brandId = activeBrand?.id

  const [llmsTxt, setLlmsTxt] = useState('')
  const [isManuallyEdited, setIsManuallyEdited] = useState(false)
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState('')
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false)
  const [error, setError] = useState('')
  const [showSuccessToast, setShowSuccessToast] = useState(false)

  useEffect(() => {
    if (brandId) {
      fetchLlmsTxt()
    }
  }, [brandId])

  const fetchLlmsTxt = async () => {
    if (!brandId) return

    try {
      setLoading(true)
      setError('')
      const response = await api.get(`/llms/${brandId}`)

      if (response.data.exists) {
        setLlmsTxt(response.data.content || '')
        setIsManuallyEdited(response.data.isManuallyEdited || false)
      } else {
        // No LLMS exists, auto-generate
        await generateLlmsTxt()
      }
    } catch (error) {
      console.error('Failed to fetch LLMS.txt:', error)
      setError('Failed to load LLMS.txt content')
    } finally {
      setLoading(false)
    }
  }

  const generateLlmsTxt = async (forceRegenerate = false) => {
    console.log('[LLMS] generateLlmsTxt called, brandId:', brandId, 'forceRegenerate:', forceRegenerate)
    if (!brandId) {
      console.log('[LLMS] No brandId, returning')
      return
    }

    // Close modal immediately when regeneration starts
    setShowRegenerateConfirm(false)

    try {
      setGenerating(true)
      setError('')
      console.log('[LLMS] Making API request to /llms/' + brandId + '/generate')
      const response = await api.post(`/llms/${brandId}/generate`, { forceRegenerate })
      console.log('[LLMS] API response:', response.data)
      setLlmsTxt(response.data.content || '')
      setIsManuallyEdited(false)

      // Track LLMS.txt generation
      trackEvent(ANALYTICS_EVENTS.LLMS_TXT_GENERATED, {
        isRegeneration: forceRegenerate,
        contentLength: (response.data.content || '').length
      })

      // Show success toast
      setShowSuccessToast(true)
      setTimeout(() => setShowSuccessToast(false), 3000)
    } catch (error) {
      console.error('[LLMS] Failed to generate LLMS.txt:', error)
      setError('Failed to generate LLMS.txt. Please try again.')
    } finally {
      setGenerating(false)
    }
  }

  const saveLlmsTxt = async () => {
    if (!brandId) return

    try {
      setSaving(true)
      setError('')
      await api.put(`/llms/${brandId}`, { content: editContent })
      setLlmsTxt(editContent)
      setIsManuallyEdited(true)
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to save LLMS.txt:', error)
      setError('Failed to save changes. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const startEditing = () => {
    setEditContent(llmsTxt)
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setEditContent('')
    setIsEditing(false)
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(llmsTxt)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const downloadFile = () => {
    const blob = new Blob([llmsTxt], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'llms.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleRegenerate = () => {
    console.log('[LLMS] handleRegenerate called, isManuallyEdited:', isManuallyEdited)
    // Always show confirmation dialog
    setShowRegenerateConfirm(true)
  }

  // Demo mode placeholder
  if (isDemoMode) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">LLMS.txt Generator</h1>
          <p className="mt-2 text-gray-600">
            Automatically generated from your brand content and enriched with AI contextual relevant narration to ensure your brand is easy to discover and compelling to AI providers.
          </p>
        </div>

        {/* Demo Placeholder */}
        <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 rounded-lg shadow-xl p-8 lg:p-12 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-full p-4 shadow-lg">
                <FileText className="h-12 w-12 text-indigo-600" />
              </div>
            </div>

            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              AI-Optimized LLMS.txt Files
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Automatically generate LLMS.txt files that help AI assistants like ChatGPT, Claude, and Gemini understand what your company does and when to recommend your brand.
            </p>

            {/* Feature Grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-8 text-left">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <Sparkles className="h-8 w-8 text-indigo-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Auto-Generated</h3>
                <p className="text-sm text-gray-600">
                  Automatically created from your imported content, products, and services with AI narration
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <Edit2 className="h-8 w-8 text-purple-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Fully Editable</h3>
                <p className="text-sm text-gray-600">
                  Manually edit the content to add your unique voice and specific messaging
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <Brain className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">AI-Ready Format</h3>
                <p className="text-sm text-gray-600">
                  Follows the LLMS.txt standard format that AI assistants can easily parse and understand
                </p>
              </div>
            </div>

            <a
              href="/register"
              className="inline-flex items-center px-8 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-lg"
            >
              Sign Up to Generate Your LLMS.txt
              <span className="ml-2">→</span>
            </a>
          </div>
        </div>

        {/* Sample Preview */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Sample LLMS.txt Preview</h2>
          </div>
          <div className="p-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800">
{`# Acme Snacks

> Organic, healthy snack foods for families

Acme Snacks offers a range of organic snack products made from clean,
sustainable ingredients. Our mission is to provide families with healthy,
delicious alternatives to traditional processed snacks.

## Products & Services

### Organic Nut Mixes
Premium mixed nuts sourced from organic farms, lightly roasted and seasoned
with natural ingredients. Perfect for on-the-go snacking.

### Fruit & Nut Bars
Wholesome energy bars combining dried fruits, nuts, and seeds. No added sugars,
preservatives, or artificial ingredients.

### Kids' Snack Packs
Portion-controlled snack packs designed for children, featuring age-appropriate
serving sizes and kid-friendly flavors.

## Values

- 100% certified organic ingredients
- Sustainable and ethical sourcing
- Compostable packaging
- Transparent supply chain

## Contact

Website: acme-snacks.com
Email: hello@acme-snacks.com`}
              </pre>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">About LLMS.txt</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <FileText className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">What is LLMS.txt?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  LLMS.txt is a standard format for helping AI assistants understand what your company does,
                  making it easier for them to recommend your brand when appropriate.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <RefreshCw className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Auto-Generated</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Content is automatically generated from your brand's products, services, and content data.
                  Re-generate anytime to reflect latest updates.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <Edit2 className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Manual Editing</h3>
                <p className="text-sm text-gray-600 mt-1">
                  You can manually edit the content to add specific details or adjust the messaging.
                  Manually edited versions won't be overwritten by auto-generation.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <Download className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Use the File</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Download and place the llms.txt file in your website's root directory at
                  <code className="text-xs bg-gray-100 px-1 rounded"> /llms.txt</code> for AI assistants to discover.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading LLMS.txt...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">LLMS.txt Generator</h1>
        <p className="mt-2 text-gray-600">
          Automatically generated from your brand content and enriched with AI conextual relevant narration to ensure your brand is easy to discover and compelling to AI providers.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Regenerate Confirmation Modal */}
      {showRegenerateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Regenerate LLMS.txt?
            </h3>
            <p className="text-gray-600 mb-4">
              {isManuallyEdited ? (
                <>
                  This will replace your <strong>manually edited version</strong> with a new auto-generated version based on your current brand content. <strong>Any manual adjustments will be lost.</strong> This action cannot be undone.
                </>
              ) : (
                <>
                  This will regenerate the LLMS.txt file with the latest content from your brand. <strong>If you have made any manual adjustments, they will be lost.</strong> This action cannot be undone.
                </>
              )}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRegenerateConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => generateLlmsTxt(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Yes, Regenerate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow">
        {/* Actions Bar */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit LLMS.txt' : 'LLMS.txt Content'}
              </h2>
              {!isEditing && (
                <>
                  {isManuallyEdited && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <Edit2 className="h-3 w-3 mr-1" />
                      Manually Edited
                    </span>
                  )}
                  {!isManuallyEdited && llmsTxt && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Auto-Generated
                    </span>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2">
              {!isEditing ? (
                <>
                  <button
                      onClick={startEditing}
                      className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                      disabled={!llmsTxt}
                  >
                    <Edit2 className="h-4 w-4 mr-1.5" />
                    Edit
                  </button>
                  {/* Regenerate button only shown in development */}
                  {import.meta.env.DEV && (
                    <button
                        onClick={handleRegenerate}
                        disabled={generating}
                        className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      <RefreshCw className={`h-4 w-4 mr-1.5 ${generating ? 'animate-spin' : ''}`} />
                      {generating ? 'Regenerating...' : 'Re-Generate'}
                    </button>
                  )}
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    disabled={!llmsTxt}
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-1.5" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1.5" />
                        Copy
                      </>
                    )}
                  </button>
                  <button
                    onClick={downloadFile}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    disabled={!llmsTxt}
                  >
                    <Download className="h-4 w-4 mr-1.5" />
                    Download
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={cancelEditing}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveLlmsTxt}
                    disabled={saving}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 mr-1.5" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6">
          {isEditing ? (
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full h-[600px] p-4 font-mono text-sm text-gray-800 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter your LLMS.txt content..."
            />
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 min-h-[600px]">
              {llmsTxt ? (
                <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800">
                  {llmsTxt}
                </pre>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-20">
                  <FileText className="h-16 w-16 text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No LLMS.txt content yet
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Click "Re-Generate" to automatically create LLMS.txt content from your brand data
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Best Practices */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">About LLMS.txt</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start">
            <FileText className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-gray-900">What is LLMS.txt?</h3>
              <p className="text-sm text-gray-600 mt-1">
                LLMS.txt is a standard format for helping AI assistants understand what your company does,
                making it easier for them to recommend your brand when appropriate.
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <RefreshCw className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-gray-900">Auto-Generated</h3>
              <p className="text-sm text-gray-600 mt-1">
                Content is automatically generated from your brand's products, services, and content data.
                Re-generate anytime to reflect latest updates.
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <Edit2 className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-gray-900">Manual Editing</h3>
              <p className="text-sm text-gray-600 mt-1">
                You can manually edit the content to add specific details or adjust the messaging.
                Manually edited versions won't be overwritten by auto-generation.
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <Download className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-gray-900">Use the File</h3>
              <p className="text-sm text-gray-600 mt-1">
                Download and place the llms.txt file in your website's root directory at
                <code className="text-xs bg-gray-100 px-1 rounded"> /llms.txt</code> for AI assistants to discover.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Toast */}
      {showSuccessToast && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className="bg-green-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
            <Check className="h-5 w-5" />
            <span className="font-medium">LLMS.txt Generated Successfully!</span>
          </div>
        </div>
      )}
    </div>
  )
}
